import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  FEATURE_STATES,
  FEATURES,
  FeatureName,
  FeatureState,
  getFeatureDisplayName,
  SELECTABLE_FEATURES,
} from "../../../constants/features";
import { hostname } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

// Base configurable features (always available)
const BASE_CONFIGURABLE_FEATURES: FeatureName[] = [
  ...SELECTABLE_FEATURES,
  FEATURES.COLLABORATORS,
  FEATURES.INTEGRATIONS,
];

// Features that require feature flag access
const FEATURE_FLAG_FEATURES: FeatureName[] = [FEATURES.AUDIENCE_BUILDER];

// Function to build feature groups based on available features
const buildFeatureGroups = (
  availableFeatures: FeatureName[]
): Record<string, FeatureName[]> => {
  const groups: Record<string, FeatureName[]> = {
    "Core Features": [
      FEATURES.DASHBOARD,
      FEATURES.CHAT,
      FEATURES.EMPLOYEE,
      FEATURES.KNOWLEDGE_BASE,
    ].filter((f) => availableFeatures.includes(f)),
    "Productivity Tools": [
      FEATURES.WORKFLOW,
      FEATURES.SMART_LIST,
      FEATURES.SEQUENCE,
      FEATURES.INBOX,
      FEATURES.CONTENT_ENGINE,
      FEATURES.AGENT,
    ].filter((f) => availableFeatures.includes(f)),
    "Content Generation": [FEATURES.IMAGE, FEATURES.AUDIO, FEATURES.VIDEO].filter(
      (f) => availableFeatures.includes(f)
    ),
    Administration: [FEATURES.COLLABORATORS, FEATURES.INTEGRATIONS].filter((f) =>
      availableFeatures.includes(f)
    ),
  };

  // Add Audience Builder group for feature flag features
  const audienceBuilderFeatures = [FEATURES.AUDIENCE_BUILDER].filter((f) =>
    availableFeatures.includes(f)
  );
  if (audienceBuilderFeatures.length > 0) {
    groups["Audience Builder"] = audienceBuilderFeatures;
  }

  // Remove empty groups
  return Object.fromEntries(
    Object.entries(groups).filter(([_, features]) => features.length > 0)
  );
};

interface OwnerInfo {
  uid: string;
  email: string;
  displayName?: string;
}

export interface OrganizationData {
  id?: string;
  name: string;
  description?: string;
  features: { [key: string]: FeatureState };
  creditLimit?: number | null;
  seats?: number | null;
  companyLimit?: number | null;
  domainLimit?: number | null;
  status?: string;
  ownerUid?: string;
  owner?: OwnerInfo | null;
  owners?: OwnerInfo[];
}

interface OrganizationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  getToken: () => Promise<string>;
  whitelabelId: string;
  editOrganization?: OrganizationData | null;
}

export default function OrganizationFormDialog({
  open,
  onClose,
  onSaved,
  getToken,
  whitelabelId,
  editOrganization,
}: OrganizationFormDialogProps) {
  const { employees } = useEmployeeTerminology();
  const isEdit = !!editOrganization?.id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creditLimit, setCreditLimit] = useState<string>("");
  const [seats, setSeats] = useState<string>("");
  const [companyLimit, setCompanyLimit] = useState<string>("");
  const [domainLimit, setDomainLimit] = useState<string>("");
  const [status, setStatus] = useState("active");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [owners, setOwners] = useState<OwnerInfo[]>([]);
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [newOwnerFirstName, setNewOwnerFirstName] = useState("");
  const [newOwnerLastName, setNewOwnerLastName] = useState("");
  const [addingOwner, setAddingOwner] = useState(false);
  const [featureStates, setFeatureStates] = useState<{
    [key: string]: FeatureState;
  }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enabledFeatureFlags, setEnabledFeatureFlags] = useState<string[]>([]);

  // Compute configurable features based on base features + enabled feature flags
  const configurableFeatures: FeatureName[] = [
    ...BASE_CONFIGURABLE_FEATURES,
    ...FEATURE_FLAG_FEATURES.filter((f) => enabledFeatureFlags.includes(f)),
  ];

  // Build feature groups based on available features
  const featureGroups = buildFeatureGroups(configurableFeatures);

  // Fetch enabled feature flags for this whitelabel
  useEffect(() => {
    const fetchEnabledFeatureFlags = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/feature-flags/enabled`, {
          params: { whitelabelId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnabledFeatureFlags(response.data.features || []);
      } catch (err) {
        console.error("Error fetching enabled feature flags:", err);
        setEnabledFeatureFlags([]);
      }
    };

    if (open && whitelabelId) {
      fetchEnabledFeatureFlags();
    }
  }, [open, whitelabelId, getToken]);

  useEffect(() => {
    if (open) {
      if (editOrganization) {
        setName(editOrganization.name || "");
        setDescription(editOrganization.description || "");
        setCreditLimit(
          editOrganization.creditLimit != null
            ? String(editOrganization.creditLimit)
            : ""
        );
        setSeats(
          editOrganization.seats != null
            ? String(editOrganization.seats)
            : ""
        );
        setCompanyLimit(
          editOrganization.companyLimit != null
            ? String(editOrganization.companyLimit)
            : ""
        );
        setDomainLimit(
          editOrganization.domainLimit != null
            ? String(editOrganization.domainLimit)
            : ""
        );
        setStatus(editOrganization.status || "active");
        setOwners(editOrganization.owners || []);
        // For existing orgs, use saved features but default feature-flag features to hidden
        // if they weren't previously configured
        const existingFeatures = editOrganization.features || {};
        const featuresWithDefaults: { [key: string]: FeatureState } = { ...existingFeatures };
        FEATURE_FLAG_FEATURES.forEach((f) => {
          if (!(f in featuresWithDefaults)) {
            featuresWithDefaults[f] = FEATURE_STATES.HIDDEN;
          }
        });
        setFeatureStates(featuresWithDefaults);
      } else {
        setName("");
        setDescription("");
        setCreditLimit("");
        setSeats("");
        setCompanyLimit("");
        setDomainLimit("");
        setStatus("active");
        setOwnerEmail("");
        setOwnerFirstName("");
        setOwnerLastName("");
        setOwners([]);
        setNewOwnerEmail("");
        setNewOwnerFirstName("");
        setNewOwnerLastName("");

        const defaults: { [key: string]: FeatureState } = {};
        configurableFeatures.forEach((f) => {
          defaults[f] = FEATURE_STATES.ENABLED;
        });
        setFeatureStates(defaults);
      }
      setError("");
    }
  }, [open, editOrganization, configurableFeatures.length]);

  const handleFeatureStateChange = (feature: string, state: FeatureState) => {
    setFeatureStates((prev) => ({ ...prev, [feature]: state }));
  };

  const handleSetAllState = (state: FeatureState) => {
    const newStates: { [key: string]: FeatureState } = {};
    configurableFeatures.forEach((f) => {
      newStates[f] = state;
    });
    setFeatureStates(newStates);
  };

  const handleAddOwner = async () => {
    if (!newOwnerEmail.trim()) return;
    setAddingOwner(true);
    try {
      const token = await getToken();
      // Add owner by email - backend resolves to UID
      const res = await axios.post(
        `${hostname}/whitelabel/${whitelabelId}/organizations/${editOrganization!.id}/owners`,
        {
          email: newOwnerEmail.trim(),
          firstName: newOwnerFirstName.trim(),
          lastName: newOwnerLastName.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewOwnerEmail("");
      setNewOwnerFirstName("");
      setNewOwnerLastName("");
      if (res.data?.owners) {
        setOwners(res.data.owners);
      }
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add owner");
    } finally {
      setAddingOwner(false);
    }
  };

  const handleRemoveOwner = async (ownerUid: string) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/organizations/${editOrganization!.id}/owners/${ownerUid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOwners((prev) => prev.filter((o) => o.uid !== ownerUid));
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove owner");
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Organization name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      const payload: any = {
        name: name.trim(),
        description: description.trim() || undefined,
        features: featureStates,
        creditLimit: creditLimit ? Number(creditLimit) : null,
        seats: seats ? Number(seats) : null,
        companyLimit: companyLimit ? Number(companyLimit) : null,
        domainLimit: domainLimit ? Number(domainLimit) : null,
      };

      if (isEdit) {
        payload.status = status;
        await axios.put(
          `${hostname}/whitelabel/${whitelabelId}/organizations/${editOrganization!.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        if (ownerEmail) {
          payload.ownerEmail = ownerEmail.trim();
          payload.ownerFirstName = ownerFirstName.trim();
          payload.ownerLastName = ownerLastName.trim();
        }
        await axios.post(
          `${hostname}/whitelabel/${whitelabelId}/organizations`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.error || "Failed to save organization";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {isEdit ? "Edit Organization" : "New Organization"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="subtitle2" fontWeight="bold">
            Organization Details
          </Typography>
          <TextField
            label="Organization Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            size="small"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
          />

          {/* Owner (create only) */}
          {!isEdit && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                Owner (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provision a user as the organization owner. Leave blank to
                create without an owner.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    fullWidth
                    size="small"
                    type="email"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="First Name"
                    value={ownerFirstName}
                    onChange={(e) => setOwnerFirstName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Last Name"
                    value={ownerLastName}
                    onChange={(e) => setOwnerLastName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Owners management (edit only) */}
          {isEdit && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                Owners
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {owners.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No owners assigned
                  </Typography>
                )}
                {owners.map((o) => (
                  <Chip
                    key={o.uid}
                    label={
                      o.displayName
                        ? `${o.displayName} (${o.email})`
                        : o.email
                    }
                    onDelete={
                      o.uid === editOrganization?.ownerUid
                        ? undefined
                        : () => handleRemoveOwner(o.uid)
                    }
                    variant={
                      o.uid === editOrganization?.ownerUid
                        ? "filled"
                        : "outlined"
                    }
                    size="small"
                    sx={{ maxWidth: "none" }}
                  />
                ))}
              </Box>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    label="Email"
                    value={newOwnerEmail}
                    onChange={(e) => setNewOwnerEmail(e.target.value)}
                    size="small"
                    type="email"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOwner();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="First Name"
                    value={newOwnerFirstName}
                    onChange={(e) => setNewOwnerFirstName(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Last Name"
                    value={newOwnerLastName}
                    onChange={(e) => setNewOwnerLastName(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <LoadingButton
                    onClick={handleAddOwner}
                    variant="outlined"
                    size="small"
                    loading={addingOwner}
                    disabled={!newOwnerEmail.trim()}
                    fullWidth
                  >
                    Add
                  </LoadingButton>
                </Grid>
              </Grid>
            </>
          )}

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
            Limits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                label="Credit Limit"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                fullWidth
                type="number"
                size="small"
                helperText="Monthly credits"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Seats"
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                fullWidth
                type="number"
                size="small"
                helperText="Max users"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Companies Limit"
                value={companyLimit}
                onChange={(e) => setCompanyLimit(e.target.value)}
                fullWidth
                type="number"
                size="small"
                helperText="Max companies"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Domain Limit"
                value={domainLimit}
                onChange={(e) => setDomainLimit(e.target.value)}
                fullWidth
                type="number"
                size="small"
                helperText="Max custom domains"
              />
            </Grid>
          </Grid>

          {isEdit && (
            <FormControl size="small" sx={{ maxWidth: 200 }}>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          )}

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
            Feature Access
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Enabled:</strong> Fully accessible &nbsp;|&nbsp;
              <strong>Locked:</strong> Visible but requires upgrade
              &nbsp;|&nbsp;
              <strong>Hidden:</strong> Completely hidden
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These features apply to all companies within this organization.
            Companies can further restrict features but cannot enable features
            that are locked or hidden at the organization level.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSetAllState(FEATURE_STATES.ENABLED)}
            >
              Enable All
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSetAllState(FEATURE_STATES.LOCKED)}
            >
              Lock All
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSetAllState(FEATURE_STATES.HIDDEN)}
            >
              Hide All
            </Button>
          </Box>

          {Object.entries(featureGroups).map(([groupName, groupFeatures]) => (
            <Box key={groupName} sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ mb: 1 }}>
                {groupName}
              </Typography>
              {groupFeatures.map((feature) => (
                <Box
                  key={feature}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 0.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <FormLabel
                    sx={{
                      fontSize: "0.85rem",
                      color: "text.primary",
                      minWidth: 150,
                    }}
                  >
                    {getFeatureDisplayName(feature as any, employees)}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={featureStates[feature] || FEATURE_STATES.ENABLED}
                    onChange={(e) =>
                      handleFeatureStateChange(
                        feature,
                        e.target.value as FeatureState
                      )
                    }
                    sx={{ gap: 2 }}
                  >
                    <FormControl>
                      <Radio
                        value={FEATURE_STATES.ENABLED}
                        size="small"
                      />
                      <Typography variant="caption" align="center">
                        Enabled
                      </Typography>
                    </FormControl>
                    <FormControl>
                      <Radio
                        value={FEATURE_STATES.LOCKED}
                        size="small"
                      />
                      <Typography variant="caption" align="center">
                        Locked
                      </Typography>
                    </FormControl>
                    <FormControl>
                      <Radio
                        value={FEATURE_STATES.HIDDEN}
                        size="small"
                      />
                      <Typography variant="caption" align="center">
                        Hidden
                      </Typography>
                    </FormControl>
                  </RadioGroup>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={loading}
        >
          {isEdit ? "Save Changes" : "Create Organization"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
